import type { FC, ReactNode } from 'react'

import { MapPin, Navigation, Phone } from 'lucide-react'
import { buttonStyles } from '@/lib/button-styles'
import { Container } from '@/components/generals/container'
import { HoursList } from '@/components/generals/hours-list'
import { OpenStatus } from '@/components/generals/open-status'
import { SectionHeading } from '@/components/generals/section-heading'
import { business } from '@/constants/business'

export const VisitSection: FC = (): ReactNode => {
    return (
        <section id='visit' aria-labelledby='visit-title' className='scroll-mt-20 bg-background py-20 sm:py-28'>
            <Container>
                <SectionHeading
                    id='visit-title'
                    eyebrow='Visit us'
                    title='Find the bay'
                    description='Pull in any time we are open, or book ahead and we will have your slot ready.'
                />

                <div className='mt-14 grid gap-6 lg:grid-cols-2'>
                    <div className='relative overflow-hidden rounded-[1.75rem] bg-ink p-7 text-white sm:p-9'>
                        <div
                            aria-hidden='true'
                            className='pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(white_1px,transparent_1px),linear-gradient(90deg,white_1px,transparent_1px)] [background-size:36px_36px]'
                        />

                        <div className='relative'>
                            <span className='grid size-14 place-items-center rounded-2xl bg-aqua text-ink'>
                                <MapPin className='size-7' />
                            </span>

                            <h3 className='mt-7 font-heading text-3xl font-bold uppercase tracking-wide'>
                                Address
                            </h3>

                            <address className='mt-3 text-lg not-italic leading-relaxed text-white/80'>
                                {business.address.map(line => (
                                    <span key={line} className='block'>
                                        {line}
                                    </span>
                                ))}
                            </address>

                            <div className='mt-8 flex flex-wrap gap-3'>
                                <a
                                    href={business.directionsUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={buttonStyles({ className: 'focus-visible:outline-aqua' })}
                                >
                                    <Navigation className='size-4' />
                                    Get directions
                                </a>

                                <a href={business.phone.href} className={buttonStyles({ variant: 'outline-light' })}>
                                    <Phone className='size-4' />
                                    {business.phone.display}
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className='rounded-[1.75rem] border border-border bg-card p-5 sm:p-7'>
                        <div className='flex flex-wrap items-center justify-between gap-3 px-2 pb-4 sm:px-4'>
                            <h3 className='font-heading text-3xl font-bold uppercase tracking-wide text-ink'>
                                Opening hours
                            </h3>

                            <OpenStatus className='shadow-none ring-ink/10' />
                        </div>

                        <HoursList />
                    </div>
                </div>
            </Container>
        </section>
    )
}
